/**
 * RBAC permission catalog and scope model. Authorization is enforced on the
 * backend using these; the frontend only uses them to hide affordances (never as
 * a security control). See ARCHITECTURE.md §8 and CONTROL_PLANE.md.
 */

/**
 * Scope levels, from broadest to narrowest. A grant at a level implies the same
 * permission on descendants. Values match the Prisma `ScopeLevel` enum so the two
 * are interchangeable across the shared/db boundary.
 */
export const ScopeLevel = {
  PLATFORM: 'PLATFORM',
  TENANT: 'TENANT',
  ORGANIZATION: 'ORGANIZATION',
  SITE_GROUP: 'SITE_GROUP',
  SITE: 'SITE',
  FABRIC: 'FABRIC',
} as const;
export type ScopeLevel = (typeof ScopeLevel)[keyof typeof ScopeLevel];

/** Ordered from broadest (0) to narrowest. Used for effective-permission resolution. */
export const SCOPE_ORDER: ScopeLevel[] = [
  ScopeLevel.PLATFORM,
  ScopeLevel.TENANT,
  ScopeLevel.ORGANIZATION,
  ScopeLevel.SITE_GROUP,
  ScopeLevel.SITE,
  ScopeLevel.FABRIC,
];

/**
 * Permission strings are `resource:action`. Keep this list the single source of
 * truth; the RBAC seeder and the `@RequirePermissions` guard both consume it.
 */
export const Permission = {
  // Identity & access
  USER_READ: 'user:read',
  USER_WRITE: 'user:write',
  ROLE_READ: 'role:read',
  ROLE_WRITE: 'role:write',
  API_TOKEN_READ: 'api_token:read',
  API_TOKEN_WRITE: 'api_token:write',

  // Tenancy structure
  ORG_READ: 'organization:read',
  ORG_WRITE: 'organization:write',
  TEAM_READ: 'team:read',
  TEAM_WRITE: 'team:write',
  SITE_READ: 'site:read',
  SITE_WRITE: 'site:write',
  SITE_GROUP_READ: 'site_group:read',
  SITE_GROUP_WRITE: 'site_group:write',
  REGION_READ: 'region:read',
  REGION_WRITE: 'region:write',

  // Edge & network
  DEVICE_READ: 'edge_device:read',
  DEVICE_WRITE: 'edge_device:write',
  NETWORK_READ: 'network:read',
  NETWORK_WRITE: 'network:write',

  // Data plane (WireGuard) & address management
  WIREGUARD_READ: 'wireguard:read',
  WIREGUARD_WRITE: 'wireguard:write',
  ADDRESS_POOL_READ: 'address_pool:read',
  ADDRESS_POOL_WRITE: 'address_pool:write',

  // Routing plane
  ROUTING_READ: 'routing:read',
  ROUTING_WRITE: 'routing:write',

  // Firewall & segmentation
  FIREWALL_READ: 'firewall:read',
  FIREWALL_WRITE: 'firewall:write',

  // Fabric & routing
  FABRIC_READ: 'fabric:read',
  FABRIC_WRITE: 'fabric:write',
  TOPOLOGY_READ: 'topology:read',
  TOPOLOGY_WRITE: 'topology:write',

  // Deployment
  DEPLOYMENT_READ: 'deployment:read',
  DEPLOYMENT_WRITE: 'deployment:write',
  DEPLOYMENT_EXECUTE: 'deployment:execute',

  // Operations
  ALERT_READ: 'alert:read',
  ALERT_WRITE: 'alert:write',
  AUDIT_READ: 'audit:read',

  // Relay (platform infrastructure, shared across tenants)
  RELAY_READ: 'relay:read',
  RELAY_WRITE: 'relay:write',

  // Federation (cross-owner and cross-organization interconnection)
  FEDERATION_READ: 'federation:read',
  FEDERATION_WRITE: 'federation:write',

  // Administration
  SETTING_READ: 'setting:read',
  SETTING_WRITE: 'setting:write',
  TENANT_ADMIN: 'tenant:admin',
  PLATFORM_ADMIN: 'platform:admin',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

export const ALL_PERMISSIONS: Permission[] = Object.values(Permission);

/** Built-in role definitions seeded per tenant (plus a platform-admin role). */
export interface RoleDefinition {
  key: string;
  name: string;
  description: string;
  permissions: Permission[];
}

const READ_PERMISSIONS = ALL_PERMISSIONS.filter((p) => p.endsWith(':read'));

export const BUILTIN_ROLES: RoleDefinition[] = [
  {
    key: 'platform-admin',
    name: 'Platform Administrator',
    description: 'Full control across all tenants. Cross-tenant actions are audited.',
    permissions: ALL_PERMISSIONS,
  },
  {
    key: 'tenant-admin',
    name: 'Tenant Administrator',
    description: 'Full control within a tenant.',
    // Relays are shared platform infrastructure: a tenant admin may view relay
    // health (relay:read, via READ_PERMISSIONS) but not reconfigure a relay that
    // other tenants also depend on. Managing the relay fleet is platform-only.
    permissions: ALL_PERMISSIONS.filter(
      (p) => p !== Permission.PLATFORM_ADMIN && p !== Permission.RELAY_WRITE,
    ),
  },
  {
    key: 'network-operator',
    name: 'Network Operator',
    description: 'Manage sites, devices, fabrics, and run deployments.',
    permissions: [
      Permission.ORG_READ,
      Permission.SITE_READ,
      Permission.SITE_WRITE,
      Permission.SITE_GROUP_READ,
      Permission.REGION_READ,
      Permission.DEVICE_READ,
      Permission.DEVICE_WRITE,
      Permission.RELAY_READ,
      Permission.NETWORK_READ,
      Permission.NETWORK_WRITE,
      Permission.WIREGUARD_READ,
      Permission.WIREGUARD_WRITE,
      Permission.ADDRESS_POOL_READ,
      Permission.ADDRESS_POOL_WRITE,
      Permission.ROUTING_READ,
      Permission.ROUTING_WRITE,
      Permission.FIREWALL_READ,
      Permission.FIREWALL_WRITE,
      Permission.FABRIC_READ,
      Permission.FABRIC_WRITE,
      Permission.TOPOLOGY_READ,
      Permission.TOPOLOGY_WRITE,
      Permission.DEPLOYMENT_READ,
      Permission.DEPLOYMENT_WRITE,
      Permission.DEPLOYMENT_EXECUTE,
      Permission.ALERT_READ,
      Permission.ALERT_WRITE,
      Permission.AUDIT_READ,
    ],
  },
  {
    key: 'read-only',
    name: 'Read Only',
    description: 'View-only access to all resources.',
    permissions: READ_PERMISSIONS,
  },
];
